<template>
  <div id="branch-tags" class="mb-24">
    <div class="flex justify-center py-10">
      <div
        id="tags-card"
        class="relative py-5 border w-180 h-min bg-pure-white border-slate-400 rounded-3xl"
      >
        <div
          id="tags-header"
          class="px-10 py-5 space-y-4 border-b border-slate-400"
        >
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <BlTextTitle3 text="Branches" />
              <BlBubbleCount :count="totalBranches || 0" />
            </div>
            <BlTextBody2
              text="Create and use branch tags to add location details for clients and prospects."
            />
          </div>
          <div
            v-if="currentUser.role.includes('admin')"
            class="flex items-center gap-4"
          >
            <BlInputText :input="tagInput" @changed="inputHandler" />
            <div class="relative">
              <div
                :data-id="`color-branch-tag`"
                :class="[
                  colorOpened ? 'border-light-purple' : 'border-slate-500'
                ]"
                class="relative flex items-center justify-between w-24 h-10 px-4 border rounded cursor-pointer bg-pure-white"
                @click.prevent="colorOpened = !colorOpened"
              >
                <div
                  class="flex-shrink-0 w-6 h-6 border rounded-full border-slate-300"
                  :style="{ backgroundColor: colorInput.backgroundColor }"
                />
                <BlIcon
                  :data-id="`color-branch-tag`"
                  class="absolute w-5 h-5 transition-all duration-300 right-4"
                  icon="chevron-down"
                  :style="{ rotate: colorOpened ? '180deg' : '0deg' }"
                />
              </div>
              <div
                :data-id="`color-branch-tag`"
                v-if="colorOpened"
                class="absolute right-0 z-10 p-4 mt-2 border rounded shadow-md w-54 bg-pure-white border-grey-200"
              >
                <div
                  :data-id="`color-branch-tag`"
                  class="grid flex-wrap grid-cols-6 gap-2"
                >
                  <button
                    :data-id="`color-branch-tag`"
                    v-for="item in colorOptions"
                    :key="item.color"
                    class="flex-shrink-0 w-6 h-6 border rounded outline-none"
                    :class="[
                      colorInput.color === item.color
                        ? 'border-light-purple'
                        : 'border-transparent'
                    ]"
                    :style="{ backgroundColor: item.backgroundColor }"
                    @click.prevent="colorInput = item"
                  />
                </div>
              </div>
            </div>
            <BlInputButton
              icon="plus"
              label="Create"
              :loading="submitLoading"
              :disabled="!tagInput.model"
              @clicked="onCreate"
            />
          </div>
        </div>
        <div id="tags-body" class="px-10 py-5 space-y-2">
          <div class="grid grid-cols-5">
            <BlSort
              class="col-span-2"
              v-model="sortInput"
              :options="sortOptions"
              :defaultValue="{ primary: 'date', secondary: 'desc' }"
              @change="onChangeSort($event)"
            />
          </div>
          <div v-if="pageLoading" class="grid h-128 place-items-center">
            <div class="blum-loader"></div>
          </div>
          <div
            v-else-if="!branches.length"
            class="flex flex-col items-center justify-center gap-6 h-128"
          >
            <BlIcon icon="tags-empty" />
            <BlTextBody1
              class="text-center"
              text="Don't see any tags yet? No problem! They'll start appearing as soon as you create a new tag."
            />
          </div>
          <div v-else>
            <TagItem
              v-for="branch in branches"
              :key="branch.id"
              :branch="branch"
            />
          </div>
        </div>
        <div v-if="totalBranches > 25" id="tags-footer" class="px-10 py-5">
          <BlInputPagination
            :page="page"
            :perPage="perPage"
            :total="totalBranches"
            @changed="changePage"
            @changed-per-page="changePerPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import TagItem from "./BranchTag.vue";

import BlBubbleCount from "../../BlBubbleCount.vue";
import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlSort from "../../BlSort.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputPagination from "../../BlInputPagination.vue";

export default {
  name: "BranchTags",
  components: {
    TagItem,
    BlBubbleCount,
    BlTextTitle3,
    BlTextBody1,
    BlTextBody2,
    BlIcon,
    BlSort,
    BlInputText,
    BlInputButton,
    BlInputPagination
  },
  data() {
    return {
      pageLoading: true,
      submitLoading: false,
      page: 1,
      perPage: 25,
      tagInput: {
        placeholder: "Type a new tag",
        model: ""
      },
      colorOpened: false,
      colorInput: { color: "#C73D3D", backgroundColor: "#FBE5E5" },
      colorOptions: [
        { color: "#327D0E", backgroundColor: "#E0FCCB" },
        { color: "#059669", backgroundColor: "#D1FAE5" },
        { color: "#708308", backgroundColor: "#FCFFC8" },
        { color: "#EC4A0A", backgroundColor: "#FFEAD5" },
        { color: "#C73D3D", backgroundColor: "#FBE5E5" },
        { color: "#AF5D30", backgroundColor: "#F3E6D2" }
      ],
      //
      sortInput: { primary: "date", secondary: "desc" },
      sortOptions: {
        primary: [
          { value: "name", label: "Name" },
          { value: "date", label: "Date created" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "branches",
      "totalBranches"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const sort = this.sortInput;
      // - sort mapping -
      // name, ascending -> name_asc
      // name, descending -> name_desc
      // date created, ascending -> oldest
      // date created, descending -> newest
      if (sort.primary === "date") {
        params += `&sort=${sort.secondary === "desc" ? "newest" : "oldest"}`;
      } else {
        params += `&sort=${sort.primary}_${sort.secondary}`;
      }
      return params;
    }
  },
  watch: {
    colorOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  mounted() {
    if (!this.enableBranch) {
      return this.$router.push("/repository");
    }

    this.pageLoading = true;
    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      // prettier-ignore
      const sorts = {
        name_asc: { primary: "name", secondary: "asc" },
        name_desc: { primary: "name", secondary: "desc" },
        oldest: { primary: "date", secondary: "asc" },
        newest: { primary: "date", secondary: "desc" }
      };
      if (temp && sorts[temp]) {
        this.sortInput = sorts[temp];
      }
    }

    this.fetchBranches();
  },
  methods: {
    ...mapActions(["getBranches", "createBranch"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (`color-branch-tag` === id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.colorOpened = false;
      }
    },
    //
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchBranches();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchBranches();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchBranches();
    },
    fetchBranches() {
      this.pageLoading = true;
      this.getBranches({ params: this.getParams }).then(() => {
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/repository${this.getParams}&tab=branch`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onCreate() {
      this.submitLoading = true;
      const data = {
        branch: {
          name: this.tagInput.model,
          color: this.colorInput.color,
          background_color: this.colorInput.backgroundColor
        }
      };
      this.createBranch(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.tagInput.model = "";
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/repository/tags/BranchTags.vue